import { useCallback, useEffect, useState } from 'react';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Button } from '@tigergraph/app-ui-lib/button';
import { PLACEMENT, StatefulPopover, TRIGGER_TYPE } from 'baseui/popover';
import { StatefulPopover as TooltipStatefulPopover } from '@tigergraph/app-ui-lib/popover';
import { useSchemaDesignerContext } from '@/contexts/schemaDesignerContext';
import { GSQLEdgeJson, TypeCheckedStatus } from '@tigergraph/tools-models/topology';
import { GLOBAL_GRAPH_NAME, HelperFunctions } from '@tigergraph/tools-models';
import { useSchema } from '@/utils/graphEditor/useMetaHook';
import { Checkbox } from '@tigergraph/app-ui-lib/checkbox';
import { expand } from 'inline-style-expand-shorthand';
import { AiOutlineGlobal } from 'react-icons/ai';
import { LoadingIndicator } from '@/components/loading-indicator';
import { useWorkspaceContext } from '@/contexts/workspaceContext';

interface GlobalTypePopoverProps {
  disabled: boolean;
  updateGlobalTypes: (globalVertexTypes: TypeCheckedStatus[], globalEdgeTypes: TypeCheckedStatus[]) => void;
}

export default function GlobalTypePopover(props: GlobalTypePopoverProps) {
  const [css, theme] = useStyletron();
  const [globalVertexTypes, setGlobalVertexTypes] = useState<{ [name: string]: TypeCheckedStatus }>({});
  const [globalEdgeTypes, setGlobalEdgeTypes] = useState<{ [name: string]: TypeCheckedStatus }>({});
  const { disabled, updateGlobalTypes } = props;
  const { currentWorkspace } = useWorkspaceContext();

  const { designerService } = useSchemaDesignerContext();
  const { data: globalSchema, isFetching } = useSchema(GLOBAL_GRAPH_NAME, currentWorkspace!);

  const disableGlobalEdgeTypeCheckbox = (edgeType: GSQLEdgeJson, localTypes: string[]): boolean => {
    if (edgeType.EdgePairs) {
      for (const pair of edgeType.EdgePairs) {
        if (localTypes.includes(pair.From) || localTypes.includes(pair.To)) {
          return true;
        }
      }
    }
    return localTypes.includes(edgeType.FromVertexTypeName) || localTypes.includes(edgeType.ToVertexTypeName);
  };

  const resetGlobalTypes = useCallback(() => {
    if (!globalSchema || isFetching) {
      return;
    }
    const globalTypesInGraph = designerService.getAllGlobalTypes();
    const localTypes = designerService.getAllLocalTypes();
    setGlobalVertexTypes(
      globalSchema.VertexTypes.reduce((prev, vertex) => {
        prev[vertex.Name] = {
          vertexOrEdgeType: vertex,
          selected: globalTypesInGraph.includes(vertex.Name),
          disabled: localTypes.includes(vertex.Name),
        };
        return prev;
      }, {} as { [name: string]: TypeCheckedStatus })
    );
    setGlobalEdgeTypes(
      globalSchema.EdgeTypes.reduce((prev, edge) => {
        prev[edge.Name] = {
          vertexOrEdgeType: edge,
          selected: globalTypesInGraph.includes(edge.Name),
          disabled: localTypes.includes(edge.Name) || disableGlobalEdgeTypeCheckbox(edge, localTypes),
        };
        return prev;
      }, {} as { [name: string]: TypeCheckedStatus })
    );
  }, [designerService, globalSchema, isFetching]);

  useEffect(() => {
    resetGlobalTypes();
  }, [resetGlobalTypes]);

  return (
    <StatefulPopover
      showArrow={false}
      placement={PLACEMENT.bottomRight}
      onOpen={() => {
        resetGlobalTypes();
      }}
      content={({ close }) => {
        if (isFetching) {
          return <LoadingIndicator message="Loading global types" />;
        }
        return (
          <div>
            <div
              className={css({
                display: 'flex',
                flexDirection: 'column',
                columnGap: '16px',
                padding: '8px 10px 10px 16px',
                borderBottom: `1px solid ${theme.colors.divider}`,
                maxHeight: '400px',
                overflow: 'auto',
              })}
            >
              {Object.keys(globalVertexTypes).length + Object.keys(globalEdgeTypes).length === 0 && (
                <div>No global types</div>
              )}
              {Object.keys(globalVertexTypes).length > 0 && (
                <div
                  className={css({
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '16px',
                  })}
                >
                  Global Vertex Types
                </div>
              )}
              {Object.keys(globalVertexTypes).length > 0 && (
                <Checkbox
                  isIndeterminate={
                    !Object.values(globalVertexTypes).every((item) => item.selected) &&
                    Object.values(globalVertexTypes).some((item) => item.selected)
                  }
                  checked={Object.values(globalVertexTypes).every((item) => item.selected)}
                  onChange={(e) => {
                    const tempGlobalVertexTypes = Object.values(globalVertexTypes).map((item) => ({
                      ...item,
                      selected: !Object.values(globalVertexTypes).every((item) => item.selected),
                    }));
                    const tempGlobalEdgeTypes = Object.values(globalEdgeTypes);
                    HelperFunctions.toggleCheckStatus(tempGlobalVertexTypes, tempGlobalEdgeTypes, true);

                    setGlobalVertexTypes(
                      tempGlobalVertexTypes.reduce((prev, item) => {
                        prev[item.vertexOrEdgeType.Name] = item;
                        return prev;
                      }, {} as { [name: string]: TypeCheckedStatus })
                    );
                    setGlobalEdgeTypes(
                      tempGlobalEdgeTypes.reduce((prev, item) => {
                        prev[item.vertexOrEdgeType.Name] = item;
                        return prev;
                      }, {} as { [name: string]: TypeCheckedStatus })
                    );
                  }}
                  overrides={{
                    Root: {
                      style: {
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        alignItems: 'center',
                      },
                    },
                  }}
                >
                  Select All Vertices
                </Checkbox>
              )}
              {Object.values(globalVertexTypes).map((item) => (
                <Checkbox
                  key={item.vertexOrEdgeType.Name}
                  checked={item.selected}
                  onChange={(e) => {
                    const tempTypeCheckedStatus = { ...item, selected: !item.selected };
                    const tempGlobalEdgeTypes = Object.values(globalEdgeTypes);
                    HelperFunctions.toggleCheckStatus([tempTypeCheckedStatus], tempGlobalEdgeTypes, true);
                    setGlobalVertexTypes({
                      ...globalVertexTypes,
                      [item.vertexOrEdgeType.Name]: tempTypeCheckedStatus,
                    });
                    setGlobalEdgeTypes(
                      tempGlobalEdgeTypes.reduce((prev, item) => {
                        prev[item.vertexOrEdgeType.Name] = item;
                        return prev;
                      }, {} as { [name: string]: TypeCheckedStatus })
                    );
                  }}
                  overrides={{
                    Root: {
                      style: {
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        alignItems: 'center',
                      },
                    },
                  }}
                >
                  {item.vertexOrEdgeType.Name}
                </Checkbox>
              ))}
              {Object.keys(globalEdgeTypes).length > 0 && (
                <div
                  className={css({
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '16px',
                  })}
                >
                  Global Edge Types
                </div>
              )}
              {Object.keys(globalEdgeTypes).length > 0 && (
                <Checkbox
                  isIndeterminate={
                    !Object.values(globalEdgeTypes).every((item) => item.selected) &&
                    Object.values(globalEdgeTypes).some((item) => item.selected)
                  }
                  checked={Object.values(globalEdgeTypes).every((item) => item.selected)}
                  onChange={() => {
                    const tempGlobalVertexTypes = Object.values(globalVertexTypes);
                    const tempGlobalEdgeTypes = Object.values(globalEdgeTypes).map((item) => ({
                      ...item,
                      selected: !Object.values(globalEdgeTypes).every((item) => item.selected),
                    }));
                    HelperFunctions.toggleCheckStatus(tempGlobalEdgeTypes, tempGlobalVertexTypes);

                    setGlobalVertexTypes(
                      tempGlobalVertexTypes.reduce((prev, item) => {
                        prev[item.vertexOrEdgeType.Name] = item;
                        return prev;
                      }, {} as { [name: string]: TypeCheckedStatus })
                    );
                    setGlobalEdgeTypes(
                      tempGlobalEdgeTypes.reduce((prev, item) => {
                        prev[item.vertexOrEdgeType.Name] = item;
                        return prev;
                      }, {} as { [name: string]: TypeCheckedStatus })
                    );
                  }}
                  overrides={{
                    Root: {
                      style: {
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        alignItems: 'center',
                      },
                    },
                  }}
                >
                  Select All Edges
                </Checkbox>
              )}
              {Object.values(globalEdgeTypes).map((item) => (
                <Checkbox
                  key={item.vertexOrEdgeType.Name}
                  checked={item.selected}
                  onChange={() => {
                    const tempTypeCheckedStatus = { ...item, selected: !item.selected };
                    const tempGlobalVertexTypes = Object.values(globalVertexTypes);
                    HelperFunctions.toggleCheckStatus([tempTypeCheckedStatus], tempGlobalVertexTypes);
                    setGlobalEdgeTypes({
                      ...globalEdgeTypes,
                      [item.vertexOrEdgeType.Name]: tempTypeCheckedStatus,
                    });
                    setGlobalVertexTypes(
                      tempGlobalVertexTypes.reduce((prev, item) => {
                        prev[item.vertexOrEdgeType.Name] = item;
                        return prev;
                      }, {} as { [name: string]: TypeCheckedStatus })
                    );
                  }}
                  overrides={{
                    Root: {
                      style: {
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        alignItems: 'center',
                      },
                    },
                  }}
                >
                  {item.vertexOrEdgeType.Name}
                </Checkbox>
              ))}
            </div>
            {Object.keys(globalVertexTypes).length + Object.keys(globalEdgeTypes).length > 0 && (
              <div
                className={css({
                  display: 'flex',
                  justifyContent: 'flex-end',
                })}
              >
                <Button
                  kind="text"
                  onClick={() => {
                    updateGlobalTypes(Object.values(globalVertexTypes), Object.values(globalEdgeTypes));
                    close();
                  }}
                  overrides={{
                    BaseButton: {
                      style: {
                        color: theme.colors['text.link'],
                        fontSize: '14px',
                        fontWeight: 600,
                      },
                    },
                  }}
                >
                  Save
                </Button>
              </div>
            )}
          </div>
        );
      }}
      overrides={{
        Body: {
          style: {
            backgroundColor: theme.colors['background.primary'],
            ...expand({
              borderRadius: '2px',
              border: `1px solid ${theme.colors.divider}`,
            }),
          },
        },
        Inner: {
          style: {
            backgroundColor: theme.colors['background.primary'],
          },
        },
      }}
    >
      <div>
        <TooltipStatefulPopover
          triggerType={TRIGGER_TYPE.hover}
          content={'Global vertex and edge types'}
          overrides={{
            Body: {
              style: {
                ...expand({
                  margin: '10px',
                }),
              },
            },
          }}
        >
          <Button
            kind="secondary"
            size="compact"
            disabled={disabled}
            aria-label="global-type-popover"
            overrides={{
              BaseButton: {
                style: {
                  ...expand({
                    padding: '2px',
                  }),
                },
              },
            }}
          >
            <AiOutlineGlobal size={18} />
          </Button>
        </TooltipStatefulPopover>
      </div>
    </StatefulPopover>
  );
}
